import { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import { useMessages } from '../../contexts/messages';
import { DateTime } from 'luxon';
import EmailEditor, { EditorRef, EmailEditorProps } from 'react-email-editor';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { useMessageTemplate } from '../../hooks/useMessageTemplate';
import { EMPTY_EDITOR_DESIGN } from '@Constants/html-templates';
import AiSnippetPickerModal from '../AiSnippetPickerModal';
// import AsyncSelect from 'react-select/async';
// import { getMemberProfile } from "../../utils/fish";

const DEFAULT_METHOD_OPTIONS = [
  { value: 'SMS', label: 'SMS' },
  { value: 'EMAIL', label: 'EMAIL' },
  { value: 'PUSH', label: 'PUSH' },
];

const OutboundMessageForm = ({
  onSubmitHandler,
  defaultValues = {},
  defaultRecipients = [],
  campaign = false,
  resetTable,
  buttonText = 'Schedule',
  isEdit = false,
}) => {
  const [editorMergeTags, setEditorMergeTags] = useState({});
  const emailEditorRef = useRef(null);
  const [templateArray, setTemplateArray] = useState([]);
  const [activeField, setActiveField] = useState(null);
  const [aiSnippetModalOpen, setAiSnippetModalOpen] = useState(false);

  const {
    segments,
    mergeTags,
    inboxes,
    fetchAllInboxes,
    fetchAllFish,
    showHTMLEditor,
    setShowHTMLEditor,
    isHtmlDesignBlank,
  } = useMessages();

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    formState: { errors, isSubmitted },
  } = useForm({
    defaultValues: {
      segmentIds: Array.isArray(defaultValues.segmentIds)
        ? defaultValues.segmentIds
        : [],
      fishIds: Array.isArray(defaultValues.fishIds)
        ? defaultValues.fishIds
        : [],
      method: defaultValues.method || 'EMAIL',
      sendTime: defaultValues.sendTime
        ? new Date(defaultValues.sendTime).toISOString().slice(0, 16)
        : '',
      campaignTitle: defaultValues.campaignTitle || '',
      messageSubject: defaultValues.messageSubject || '',
      messageBody: defaultValues.messageBody || '',
      senderId: defaultValues.senderId || null,
      sendNow: true,
      html: defaultValues.html || null,
      htmlEditorJson: defaultValues.htmlEditorJson || EMPTY_EDITOR_DESIGN,
    },
  });

  const method = watch('method');
  const hasCampaignTitle = watch('campaignTitle')?.length > 0;
  const hasSender = watch('senderId');
  const hasRecipients =
    watch('segmentIds')?.length > 0 || watch('fishIds')?.length > 0;
  const hasMessageSubject = watch('messageSubject')?.length > 0;

  const [selectedContactOptions, setSelectedContactOptions] = useState(
    defaultValues?.fishIds?.length ? defaultRecipients : [],
  );

  const { data: payload } = useMessageTemplate({
    searchParams: {
      '[filter][method]': method,
      quickMessage: false,
    },
  });

  useEffect(() => {
    if (payload?.data) {
      setTemplateArray(payload.data);
    }
  }, [payload]);

  const customStyles = {
    control: (styles) => ({
      ...styles,
      borderRadius: '8px',
    }),
  };

  const mergeTagOptions = mergeTags.map((tag) => ({
    value: `[[${tag.id}]]`,
    label: tag.label,
  }));

  const messageTemplateOptions = templateArray?.map((template) => {
    let designJson = EMPTY_EDITOR_DESIGN;
    if (
      template.htmlEditorJson &&
      Object.keys(template.htmlEditorJson).length > 0
    ) {
      designJson = template.htmlEditorJson;
    }
    return {
      id: template.id,
      value: template.messageBody,
      subject: template.messageSubject,
      html: template.html ?? '',
      htmlEditorJson: designJson,
      method: template.method,
      label: template.name,
    };
  });

  const segmentOptions = segments.map((segment) => ({
    value: segment.id,
    label: segment.name,
  }));

  // Filter inboxes based on the selected method
  const filteredInboxes = inboxes.filter((inbox) => {
    if (inbox.fishId === null) {
      //exclude fish inboxes
      if (method === 'SMS') return inbox.phoneNumber;
      if (method === 'EMAIL') return inbox.email;
      return true;
    }
    return false;
  });

  const inboxOptions = filteredInboxes.map((inbox) => ({
    value: inbox.id,
    label: `${method === 'SMS' ? inbox.phoneNumber : inbox.email} ${inbox.user?.fullDisplayName ? `(${inbox.user?.fullDisplayName})` : ''}`,
  }));

  const handleTemplateChange = (selected) => {
    if (!selected) return;
    setValue('messageBody', selected.value);
    setValue('messageSubject', selected.subject);
    setValue('html', selected.html || '');
    setValue('htmlEditorJson', selected.htmlEditorJson || EMPTY_EDITOR_DESIGN);

    const unlayer = emailEditorRef.current?.editor;
    if (!unlayer) return;
    unlayer.loadDesign(selected.htmlEditorJson);
  };

  const handleMergeTagChange = (selected) => {
    if (!selected) return;

    if (activeField === 'body') {
      const messageBody = watch('messageBody') || '';
      const textArea = document.querySelector('textarea[name="messageBody"]');

      const start = textArea.selectionStart;
      const end = textArea.selectionEnd;

      const newValue =
        messageBody.substring(0, start) +
        selected.value +
        messageBody.substring(end);

      setValue('messageBody', newValue);

      setTimeout(() => {
        textArea.focus();
        textArea.setSelectionRange(
          start + selected.value.length,
          start + selected.value.length,
        );
      }, 0);
    } else if (activeField === 'subject') {
      const messageSubject = watch('messageSubject') || '';
      const input = document.querySelector('input[name="messageSubject"]');
      const start = input.selectionStart;
      const end = input.selectionEnd;

      const newValue =
        messageSubject.substring(0, start) +
        selected.value +
        messageSubject.substring(end);

      setValue('messageSubject', newValue);

      setTimeout(() => {
        input.focus();
        input.setSelectionRange(
          start + selected.value.length,
          start + selected.value.length,
        );
      }, 0);
    } else {
      const messageBody = watch('messageBody') || '';
      setValue('messageBody', messageBody + selected.value);
    }
  };

  const exportEmail = async () => {
    const unlayer = emailEditorRef.current?.editor;
    if (!unlayer) return;

    const data = await new Promise((resolve) => {
      unlayer.exportHtml((d) => resolve(d));
    });

    if (isHtmlDesignBlank(data.design)) {
      setValue('html', null);
      setValue('htmlEditorJson', null);

      return { html: null, design: null };
    } else {
      setValue('html', data.html);
      setValue('htmlEditorJson', data.design);

      return { html: data.html, design: data.design };
    }
  };

  const onReady = (unlayer) => {
    const design = getValues('htmlEditorJson');
    if (design) {
      unlayer.loadDesign(design);
    }
  };

  const onSubmit = async (data) => {
    if (data.method === 'EMAIL') {
      const htmlObj = await exportEmail();
      data.html = htmlObj?.html;
      data.htmlEditorJson = htmlObj?.design;
    }

    const sendTime = data.sendTime || new Date();
    const sendTimeUTC = new Date(sendTime).toISOString();
    const updatedData = {
      ...data,
      sendTime: sendTimeUTC,
    };

    await onSubmitHandler(updatedData).then(reset);

    resetTable();

    setShowHTMLEditor(false);
  };

  // const loadOptions = async (search) => {
  //   return new Promise((resolve) => {
  //     fetchAllFish(search).then((data) => {
  //       // console.log('data', data);
  //       const formattedData = data.map((fish) => {
  //         const profile = getMemberProfile(fish) || fish.fishProfiles[0];

  //         return {
  //           value: fish.id,
  //           label: `${profile.fName || ""} ${profile.lName || ""} (${method === 'EMAIL' ? profile.email || "No email" : fish.phoneNumber || "No phone number"
  //             })`.trim(),
  //         };
  //       });

  //       // console.log('formatted', formattedData);

  //       resolve(formattedData);

  //     })
  //   });
  // };

  useEffect(() => {
    if (!mergeTagOptions.length) return;
    const editorTagObj = {};

    mergeTagOptions.forEach((mergeTag) => {
      const name = mergeTag.label.replaceAll(' ', '_').toLowerCase();

      editorTagObj[name] = {
        name: mergeTag.label,
        value: `{{${mergeTag.value.slice(2, -2)}}}`,
        sample: 'Answer',
      };
    });

    setEditorMergeTags(editorTagObj);
  }, []);

  useEffect(() => {
    fetchAllInboxes();
    fetchAllFish();
  }, []);

  // Reset senderId when method changes to ensure compatibility
  // But only if we're not in edit mode (defaultValues.senderId is empty)
  useEffect(() => {
    if (!defaultValues.senderId) {
      setValue('senderId', undefined);
    }
  }, [method, setValue, defaultValues.senderId]);

  useEffect(() => {
    if (watch('sendNow') === true) {
      const localDateTime = DateTime.local();

      setValue('sendTime', localDateTime);
    }
  }, [watch('sendNow')]);

  useEffect(() => {
    if (method === 'EMAIL') {
      if (isEdit) {
        if (getValues('html')) {
          setShowHTMLEditor(true);
        }
      } else {
        setShowHTMLEditor(true);
      }
    }
  }, [method]);

  return (
    <div className="">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          {campaign && (
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700">
                Campaign Title
              </label>
              <Controller
                name="campaignTitle"
                control={control}
                rules={{ required: `A campaign title is required` }}
                render={({ field }) => (
                  <input
                    type="text"
                    {...field}
                    className={`mt-1 block w-full rounded-md border border-gray-300 px-2 py-1 shadow-sm ${
                      errors.campaignTitle ? 'border-red-500' : ''
                    }`}
                  />
                )}
              />

              {errors.campaignTitle && (
                <p className="text-sm text-red-500">
                  {errors.campaignTitle.message}
                </p>
              )}
            </div>
          )}

          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700">
              Communication Channel
            </label>
            <Controller
              name="method"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  value={
                    DEFAULT_METHOD_OPTIONS.find(
                      (option) => option.value === value,
                    ) || null
                  }
                  onChange={(selected) => {
                    if (selected.value === 'EMAIL') {
                      setShowHTMLEditor(true);
                    } else {
                      setShowHTMLEditor(false);
                    }
                    return onChange(selected?.value || '');
                  }}
                  options={DEFAULT_METHOD_OPTIONS}
                  placeholder="Select method..."
                  classNamePrefix="select"
                  styles={customStyles}
                  noOptionsMessage={() => 'No methods found'}
                  className={errors.method ? 'border-red-500' : ''}
                />
              )}
            />
          </div>

          {method !== 'PUSH' && (
            <>
              <label className="block text-sm font-medium text-gray-700">
                From (Sender)
              </label>
              <Controller
                name="senderId"
                control={control}
                rules={{ required: 'Sender is required' }}
                render={({ field: { value, onChange } }) => (
                  <Select
                    value={
                      inboxOptions.find((option) => option.value === value) ||
                      null
                    }
                    onChange={(selected) => onChange(selected?.value || '')}
                    options={inboxOptions}
                    placeholder="Select sender inbox..."
                    classNamePrefix="select"
                    styles={customStyles}
                    noOptionsMessage={() => 'No inboxes found'}
                    className={errors.senderId ? 'border-red-500' : ''}
                  />
                )}
              />
            </>
          )}

          {errors.senderId && (
            <p className="text-sm text-red-500">{errors.senderId.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Segments
          </label>
          <Controller
            name="segmentIds"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Select
                isMulti
                value={
                  Array.isArray(value)
                    ? value.map((id) => {
                        const segment = segments.find((s) => s.id === id);
                        return { value: id, label: segment?.name || id };
                      })
                    : []
                }
                onChange={(selected) =>
                  onChange(selected?.map((option) => option.value) || [])
                }
                options={segmentOptions}
                placeholder="Select segments..."
                classNamePrefix="select"
                styles={customStyles}
                noOptionsMessage={() => 'No segments found'}
              />
            )}
          />
        </div>

        {/* Individual Contact Selector for Campaigns */}
        {/* <div>
          <label className="block text-sm font-medium text-gray-700">Individual Contact</label>
          <Controller
            name="fishIds"
            control={control}
            render={({ field: { value, onChange } }) => (
              <AsyncSelect
                isMulti
                loadOptions={loadOptions}
                value={selectedContactOptions}
                onChange={(selected) => {
                  setSelectedContactOptions(selected || []);
                  onChange(selected?.map((option) => option.value) || []);
                }}
                placeholder="Select contact..."
                classNamePrefix="select"
                styles={customStyles}
                noOptionsMessage={() => "No fish found"}
              />
            )}
          />
        </div> */}

        <Controller
          name="sendNow"
          control={control}
          render={({ field: { value, onChange } }) => (
            <div className="mt-4 mb-2 flex items-center gap-4">
              <span
                className={value ? 'font-bold text-[#CAAB6A]' : 'text-gray-400'}
              >
                Send Now
              </span>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={!value}
                  onChange={() => onChange(!value)}
                />
                <div className="peer h-6 w-11 rounded-full bg-gray-300 peer-focus:outline-none after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full peer-checked:after:border-white"></div>
              </label>
              <span
                className={
                  !value ? 'font-bold text-[#CAAB6A]' : 'text-gray-400'
                }
              >
                Schedule
              </span>
            </div>
          )}
        />

        {!watch('sendNow') && (
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Send Time
            </label>
            <Controller
              name="sendTime"
              control={control}
              rules={{ required: 'Send time is required' }}
              render={({ field }) => {
                const localDateTime =
                  DateTime.local().toFormat("yyyy-LL-dd'T'HH:mm");

                return (
                  <input
                    type="datetime-local"
                    {...field}
                    min={localDateTime}
                    className={`mt-1 block w-full rounded-md border-gray-300 px-2 py-1 shadow-sm ${
                      errors.sendTime ? 'border-red-500' : ''
                    }`}
                  />
                );
              }}
            />
            {errors.sendTime && (
              <p className="text-sm text-red-500">{errors.sendTime.message}</p>
            )}
          </div>
        )}

        {method === 'EMAIL' && (
          <div className="mt-4 mb-2 flex items-center gap-4">
            <div className="flex w-fit justify-center">
              <span
                className={
                  showHTMLEditor === false
                    ? 'font-bold text-[#CAAB6A]'
                    : 'text-gray-400'
                }
              >
                Message
              </span>
            </div>
            <label className="relative inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                className="peer sr-only"
                checked={showHTMLEditor === true}
                onChange={async () => {
                  if (showHTMLEditor) {
                    await exportEmail();
                  }
                  setShowHTMLEditor(!showHTMLEditor);
                }}
              />
              <div className="peer h-6 w-11 rounded-full bg-gray-300 peer-focus:outline-none after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full peer-checked:after:border-white"></div>
            </label>
            <div className="flex w-fit justify-center">
              <span
                className={
                  showHTMLEditor === true
                    ? 'font-bold text-[#CAAB6A]'
                    : 'text-gray-400'
                }
              >
                HTML
              </span>
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between">
            {method !== 'SMS' && (
              <label className="block text-sm font-medium text-gray-700">
                {method === 'EMAIL' ? 'Message Subject' : 'Push Title'}
              </label>
            )}
            <div className="flex gap-2">
              <Select
                options={mergeTagOptions}
                onChange={handleMergeTagChange}
                placeholder="Insert Merge Tag"
                isClearable
                className="w-48"
                classNamePrefix="select"
                value={null}
              />

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setAiSnippetModalOpen(true)}
              >
                Insert AI Snippet
              </Button>

              <Select
                options={messageTemplateOptions}
                onChange={handleTemplateChange}
                placeholder="Templates"
                isClearable
                className="w-48"
                classNamePrefix="select"
                value={null}
              />
            </div>
          </div>

          <AiSnippetPickerModal
            open={aiSnippetModalOpen}
            onOpenChange={setAiSnippetModalOpen}
            previewFishId={(watch('fishIds') || [])[0]}
            onInsert={(tag) => handleMergeTagChange({ value: tag })}
          />

          {method !== 'SMS' && (
            <>
              <Controller
                name="messageSubject"
                control={control}
                rules={{
                  required: `A ${method === 'EMAIL' ? 'message subject' : 'push title'} is required`,
                }}
                render={({ field }) => (
                  <input
                    type="text"
                    onFocus={() => setActiveField('subject')}
                    {...field}
                    className={`mt-1 block w-full rounded-md border border-gray-300 px-2 py-1 shadow-sm ${
                      errors.messageSubject ? 'border-red-500' : ''
                    }`}
                  />
                )}
              />

              {errors.messageSubject && (
                <p className="text-sm text-red-500">
                  {errors.messageSubject.message}
                </p>
              )}
            </>
          )}
        </div>

        <label className="block text-sm font-medium text-gray-700">
          Message Body
        </label>

        {!showHTMLEditor && (
          <div>
            <Controller
              name="messageBody"
              control={control}
              rules={{ required: 'Message body is required' }}
              render={({ field }) => (
                <textarea
                  onFocus={() => setActiveField('body')}
                  rows="5"
                  {...field}
                  className={`mt-1 block w-full rounded-md border border-gray-300 px-2 py-1 shadow-sm ${
                    errors.messageBody ? 'border-red-500' : ''
                  }`}
                />
              )}
            />

            {errors.messageBody && (
              <p className="text-sm text-red-500">
                {errors.messageBody.message}
              </p>
            )}
          </div>
        )}

        <div className={`border-1 ${showHTMLEditor ? 'block' : 'hidden'}`}>
          <EmailEditor
            key={JSON.stringify(Object.keys(editorMergeTags))}
            ref={emailEditorRef}
            onReady={onReady}
            options={{
              displayMode: 'email',
              appearance: {
                backgroundColor: '#ffffff',
                canvasBackgroundColor: '#ffffff',
                panels: {
                  tools: {
                    dock: 'left',
                  },
                },
              },
              mergeTags: editorMergeTags,
            }}
          />
        </div>

        {/* Form-level validation messages - only show after submission attempt */}
        {isSubmitted && !hasCampaignTitle && (
          <p className="text-center text-sm text-red-500">
            A campaign title is required
          </p>
        )}
        {isSubmitted && !hasRecipients && (
          <p className="text-center text-sm text-red-500">
            At least one recipient (segment or contact) is required
          </p>
        )}
        {isSubmitted && method !== 'PUSH' && !hasSender && (
          <p className="text-center text-sm text-red-500">
            A sender inbox must be selected
          </p>
        )}
        {isSubmitted && method !== 'SMS' && !hasMessageSubject && (
          <p className="text-center text-sm text-red-500">
            A {method === 'EMAIL' ? 'message subject' : 'push title'} is
            required
          </p>
        )}

        <Button
          type="submit"
          className=""
          disabled={
            (isSubmitted && !hasRecipients) || (isSubmitted && !hasSender)
          }
        >
          {watch('sendNow') ? 'Send' : buttonText}
        </Button>
      </form>
    </div>
  );
};

export default OutboundMessageForm;
